<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { useMachine } from "@xstate/vue"
import { useI18n } from "vue-i18n"
import UsageProfileOptions from "@modules/usage-profile/components/UsageProfileOptions.vue"
import UsageProfileExist from "@modules/usage-profile/components/UsageProfileExist.vue"
import UsageProfileEdit from "@modules/usage-profile/components/edit/UsageProfileEdit.vue"
import { createUsageProfileFactoryMachine } from "./usage-profile-factory.machine"
import type { UsageProfile } from "@modules/usage-profile/types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            heading1: "Profils d'utilisation - {state}",
            heading_options: "Choisir",
            heading_exist: "Profil existant",
            heading_edit: "Création / Édition",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Identifiant unique du dialog */
        dialogId?: string
        /** État initial : edit | replace | '' (options) */
        fromState?: string
        /** Identifiant du bâtiment */
        buildingId: string
        /** Nom du bâtiment */
        buildingName?: string
        /** Identifiant de l'entreprise */
        companyId: string
        /** Profil en cours d'édition (null = création) */
        profile?: UsageProfile | null
    }>(),
    {
        dialogId: "dialog-usage-profile-factory",
        fromState: "",
        buildingName: "",
        profile: null,
    }
)

const emit = defineEmits<{
    close: []
    "done:form": [payload: { profile: UsageProfile }]
}>()

const visible = defineModel<boolean>("visible", { default: false })

// ================================
// MACHINE
// ================================
/** Ferme le dialog et émet close */
function onCloseDialog(): void {
    visible.value = false
    emit("close")
}

const machine = createUsageProfileFactoryMachine({
    fromState: props.fromState,
    profile: props.profile ?? null,
    modify: !!props.profile,
    onCloseDialog,
})

const { snapshot, send } = useMachine(machine, {
    input: undefined,
})

// ================================
// DONNÉES CALCULÉES
// ================================
const currentState = computed(() => snapshot.value.value as string)
const contextProfile = computed(
    () =>
        snapshot.value.context.profile as UsageProfile | { name: string } | null
)

const getStateLabel = computed(() => {
    switch (currentState.value) {
        case "UsageProfileOptions":
            return t("heading_options")
        case "UsageProfileExist":
            return t("heading_exist")
        case "UsageProfileEdit":
            return t("heading_edit")
        default:
            return ""
    }
})

const getDynamicProps = computed(() => {
    switch (currentState.value) {
        case "UsageProfileEdit":
            return {
                component: UsageProfileEdit,
                props: {
                    buildingId: props.buildingId,
                    companyId: props.companyId,
                    profile: contextProfile.value,
                },
                on: {
                    "click:create": (profile: UsageProfile) => {
                        send({ type: "CLOSE" })
                        emit("done:form", { profile })
                    },
                    "click:modify": (profile: UsageProfile) => {
                        send({ type: "CLOSE" })
                        emit("done:form", { profile })
                    },
                },
            }
        case "UsageProfileExist":
            return {
                component: UsageProfileExist,
                props: {
                    buildingId: props.buildingId,
                    companyId: props.companyId,
                    buildingName: props.buildingName,
                    profileId: props.profile?._id ?? "",
                },
                on: {
                    "click:new": () => send({ type: "EDIT" }),
                    done: ({ profile }: { profile: UsageProfile }) => {
                        send({ type: "CLOSE" })
                        emit("done:form", { profile })
                    },
                },
            }
        default:
            return {
                component: UsageProfileOptions,
                props: {},
                on: {
                    "click:exist": () => send({ type: "REPLACE" }),
                    "click:create": () => send({ type: "EDIT" }),
                },
            }
    }
})

// ================================
// WATCHERS
// ================================
watch(visible, (isVisible) => {
    if (isVisible) {
        send({ type: "UPDATE_FROM_STATE", value: props.fromState })
        send({ type: "UPDATE_PROFILE", value: props.profile ?? null })
        send({ type: "REDIRECT" })
    }
})

watch(
    () => props.fromState,
    (val) => send({ type: "UPDATE_FROM_STATE", value: val })
)
watch(
    () => props.profile,
    (val) => send({ type: "UPDATE_PROFILE", value: val ?? null })
)
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :header="t('heading1', { state: getStateLabel })"
        :style="{ width: '50vw' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        :closable="true"
        :close-on-escape="true"
        @hide="send({ type: 'CLOSE' })"
    >
        <component
            v-if="currentState !== 'redirect'"
            :is="getDynamicProps.component"
            v-bind="getDynamicProps.props"
            v-on="getDynamicProps.on"
        />
    </BaseDialog>
</template>
